import React, { useState } from 'react';
import { ChevronRight, Zap, Settings, Award, Users, ArrowRight, Check, Star, TrendingUp, Shield, Cpu, Droplets, Timer, Target, Wrench } from 'lucide-react';
import img1 from '../components/images/LT/LT800pro.png'
import img2 from '../components/images/LT/i9std.png'
import img3 from '../components/images/LT/LT980.png'
import video1 from '../components/images/LT/LT800.mp4'
import video2 from '../components/images/LT/i9.mp4'

const LeadtechPrinters = () => {
  const [activeTab, setActiveTab] = useState('LT800');
  const [hoveredCard, setHoveredCard] = useState(null);

  const printerSeries = {
    LT800: {
      name: 'LT800 Series',
      tagline: 'Precision Meets Performance',
      description: 'Professional-grade continuous inkjet printing for demanding industrial applications',
      features: [
        'High-resolution printing up to 600 DPI',   
        'Advanced ink circulation system',
        'Multi-language touch interface',
        'Automatic printhead cleaning',
        'Remote monitoring capabilities',
        'Dustproof IP65 rating'
      ],
      applications: ['Manufacturing', 'Food & Beverage', 'Pharmaceuticals', 'Automotive'],
      highlight: 'Most Popular',
      gradient: 'from-blue-600 to-purple-700',
      video: video1
    },
    LT900: {
      name: 'LT900 Series',
      tagline: 'Industrial Excellence Redefined',
      description: 'Heavy-duty continuous inkjet solution engineered for maximum uptime and reliability',
      features: [
        'Ultra-fast printing speeds',
        'Dual-cartridge system',
        'Predictive maintenance alerts',
        '24/7 continuous operation',
        'Advanced quality control',
        'Integrated barcode scanning'
      ],
      applications: ['Heavy Industry', 'Logistics', 'Packaging', 'Electronics'],
      highlight: 'Enterprise Grade',
      gradient: 'from-emerald-600 to-teal-700'
    },
    L9: {
      name: 'L9 Series',
      tagline: 'Next-Generation Innovation',
      description: 'Cutting-edge technology with smart features for the future of industrial printing',
      features: [
        'AI-powered print optimization',
        'Cloud connectivity & analytics',
        'Eco-friendly ink formulation',
        'Modular expandable design',
        'Voice command integration',
        'Sustainable operation modes'
      ],
      applications: ['Smart Manufacturing', 'IoT Integration', 'Green Production', 'Tech Industries'],
      highlight: 'Latest Technology',
      gradient: 'from-orange-600 to-red-700',
      video: video2
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black text-white overflow-hidden font-poppins">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Hero Section */}
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 text-white flex items-center justify-center px-4 py-20">
      <div className="max-w-7xl mx-auto">
        <div className="relative z-10 pt-10 pb-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-full px-6 py-2 mb-8 backdrop-blur-sm">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-medium">Industry Leading Technology</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent leading-tight">
              Leadtech
              <br />
              <span className="text-3xl md:text-5xl font-light">Continuous Inkjet Printers</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed font-poppins">
              Revolutionize your production line with our advanced continuous inkjet printing solutions. 
              Engineered for precision, built for reliability,<br/> designed for the future.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <button className="group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl">
                Explore Our Solutions
                <ArrowRight className="w-5 h-5 ml-2 inline-block group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="group border border-gray-600 hover:border-gray-400 px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:bg-white/5 backdrop-blur-sm">
                Request Demo
                <ChevronRight className="w-5 h-5 ml-2 inline-block group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
          
          {/* Image Gallery Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 px-6">
            {/* Image 1 */}
            <div className="image-card group rounded-2xl overflow-hidden relative">
              <div className="image-inner h-80 relative">
                <img 
                  src={img1} 
                  alt="Precision Printing Technology" 
                  className="object-cover w-full h-full "
                />
                <div className="image-overlay absolute inset-0 flex items-end p-6">
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white">Precision Engineering</h3>
                    <p className="text-blue-200">Micro-precision printing for industrial applications</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 w-16 h-16 rounded-full bg-blue-600/20 backdrop-blur-sm flex items-center justify-center float-animation">
                <div className="w-8 h-8 bg-blue-500 rounded-full spin-animation"></div>
              </div>
            </div>
            
            {/* Image 2 */}
            <div className="image-card group rounded-2xl overflow-hidden relative">
              <div className="image-inner h-80 relative">
                <img 
                  src={img2} 
                  alt="Advanced Industrial Printer" 
                  className="object-cover w-full h-full"
                />
                <div className="image-overlay absolute inset-0 flex items-end p-6">
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white">Advanced Technology</h3>
                    <p className="text-purple-200">Cutting-edge continuous inkjet systems</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 w-16 h-16 rounded-full bg-purple-600/20 backdrop-blur-sm flex items-center justify-center float-animation">
                <div className="w-8 h-8 bg-purple-500 rounded-full spin-animation"></div>
              </div>
            </div>
            
            {/* Image 3 */}
            <div className="image-card group rounded-2xl overflow-hidden relative">
              <div className="image-inner h-80 relative">
                <img 
                  src={img3} 
                  alt="Automated Production Line" 
                  className="object-cover w-full h-full"
                />
                <div className="image-overlay absolute inset-0 flex items-end p-6">
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white">Production Efficiency</h3>
                    <p className="text-cyan-200">Streamline your manufacturing process</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 w-16 h-16 rounded-full bg-cyan-600/20 backdrop-blur-sm flex items-center justify-center float-animation">
                <div className="w-8 h-8 bg-cyan-500 rounded-full pulse-animation"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .image-card {
          transition: all 0.4s ease;
          transform-style: preserve-3d;
        }
        
        .image-card:hover {
          transform: translateY(-10px) scale(1.02);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
        
        .image-inner {
          transition: transform 0.6s;
        }
        
        .image-card:hover .image-inner {
          transform: rotateY(5deg) rotateX(5deg);
        }
        
        .image-overlay {
          background: linear-gradient(to top, rgba(30, 41, 59, 0.9) 0%, transparent 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .image-card:hover .image-overlay {
          opacity: 1;
        }
        
        .float-animation {
          animation: float 6s ease-in-out infinite;
        }
        
        .pulse-animation {
          animation: pulse 2s ease-in-out infinite alternate;
        }
        
        .spin-animation {
          animation: spin 15s linear infinite;
        }
        
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          100% { transform: scale(1.2); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .image-card::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          transform: rotate(30deg);
          background: linear-gradient(to right, 
            rgba(255, 255, 255, 0) 0%, 
            rgba(255, 255, 255, 0.2) 50%, 
            rgba(255, 255, 255, 0) 100%);
          animation: shine 3s infinite;
          opacity: 0;
        }
        
        @keyframes shine {
          0% { opacity: 0; left: -50%; }
          10% { opacity: 1; }
          100% { opacity: 0; left: 150%; }
        }
        
        .image-card:hover::after {
          animation: shine 1.5s;
        }
      `}</style>
    </div>

      {/* Product Tabs */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 mb-20">
        <div className="flex justify-center mb-12">
          <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-2 border border-gray-700/50">
            {Object.keys(printerSeries).map((series) => (
              <button
                key={series}
                onClick={() => setActiveTab(series)}
                className={`px-8 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeTab === series
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                {series}
              </button>
            ))}
          </div>
        </div>

        {/* Active Product Display */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-full px-4 py-1 mb-4">
                <span className="text-sm font-medium text-orange-300">{printerSeries[activeTab].highlight}</span>
              </div>
              <h2 className="text-4xl font-bold mb-4">{printerSeries[activeTab].name}</h2>
              <p className="text-xl text-blue-300 font-medium mb-6">{printerSeries[activeTab].tagline}</p>
              <p className="text-gray-300 text-lg leading-relaxed">{printerSeries[activeTab].description}</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Key Features
              </h3>
              <div className="grid gap-3">
                {printerSeries[activeTab].features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 group">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full group-hover:scale-125 transition-transform"></div>
                    <span className="text-gray-300 group-hover:text-white transition-colors">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-400" />
                Applications
              </h3>
              <div className="flex flex-wrap gap-2">
                {printerSeries[activeTab].applications.map((app, index) => (
                  <span key={index} className="bg-gray-800/50 border border-gray-600/50 px-4 py-2 rounded-lg text-sm hover:bg-gray-700/50 transition-colors">
                    {app}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className={`bg-gradient-to-br ${printerSeries[activeTab].gradient} p-8 rounded-3xl shadow-2xl transform hover:scale-105 transition-all duration-500`}>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                <div className="w-full h-64 bg-gray-800/30 rounded-xl flex items-center justify-center mb-6 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
                  <div className="text-center">
                    {/* <Cpu className="w-16 h-16 mx-auto mb-4 text-white/70" />
                    <p className="text-white/70 font-medium">{printerSeries[activeTab].name}</p>
                    <p className="text-white/50 text-sm mt-2">Premium Industrial Printer</p> */}

                    <video src={printerSeries[activeTab].video} autoPlay></video>
                  </div>
                  <div className="absolute top-4 right-4 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                </div>
                <div className="text-center">
                  <button className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105">
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 mb-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-6">Why Choose Leadtech?</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Experience the perfect blend of innovation, reliability, and performance
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: <TrendingUp className="w-8 h-8" />,
              title: 'Increased Productivity',
              description: 'Boost your production efficiency with high-speed, continuous operation capabilities.',
              color: 'from-blue-500 to-cyan-500'
            },
            {
              icon: <Shield className="w-8 h-8" />,
              title: 'Unmatched Reliability',
              description: 'Built for 24/7 operation with minimal maintenance requirements and maximum uptime.',
              color: 'from-emerald-500 to-teal-500'
            },
            {
              icon: <Settings className="w-8 h-8" />,
              title: 'Easy Integration',
              description: 'Seamlessly integrate with your existing production lines and control systems.',
              color: 'from-purple-500 to-pink-500'
            },
            {
              icon: <Droplets className="w-8 h-8" />,
              title: 'Advanced Ink System',
              description: 'Proprietary ink formulations for superior print quality and durability.',
              color: 'from-orange-500 to-red-500'
            },
            {
              icon: <Timer className="w-8 h-8" />,
              title: 'Real-time Monitoring',
              description: 'Monitor performance, predict maintenance needs, and optimize operations remotely.',
              color: 'from-indigo-500 to-blue-500'
            },
            {
              icon: <Wrench className="w-8 h-8" />,
              title: 'Expert Support',
              description: 'Comprehensive technical support and training from our experienced team.',
              color: 'from-teal-500 to-green-500'
            }
          ].map((feature, index) => (
            <div
              key={index}
              className="group bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 hover:bg-gray-800/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <div className="text-white">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4 group-hover:text-white transition-colors">{feature.title}</h3>
              <p className="text-gray-400 group-hover:text-gray-300 transition-colors leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative z-10 bg-gradient-to-r from-blue-900/30 to-purple-900/30 backdrop-blur-lg border-y border-gray-700/50">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Production?</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-12">
            Join thousands of manufacturers who trust Leadtech for their industrial printing needs
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button className="group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-10 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl">
              Get Expert Consultation
              <Users className="w-5 h-5 ml-2 inline-block group-hover:scale-110 transition-transform" />
            </button>
            <button className="group border border-gray-600 hover:border-gray-400 px-10 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:bg-white/5 backdrop-blur-sm">
              Download Specifications
              <ArrowRight className="w-5 h-5 ml-2 inline-block group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          
          <div className="mt-12 flex justify-center items-center gap-8 text-gray-400">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-400" />
              <span>Industry Certified</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span>2 Year Warranty</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadtechPrinters;